/**
 * Design tokens — dense terminal / trading-desk console.
 *
 * Framework-free. No MUI, no emotion, no React. `src/theme/index.js` consumes this
 * to build the MUI theme; charts and CSS modules can consume it directly.
 *
 * PROVENANCE — every value below traces to one of four sources. Nothing is eyeballed.
 *   [REF]   dataviz reference palette (references/palette.md): categorical slots,
 *           sequential blue ramp, diverging pair, fixed status palette, chrome/ink roles.
 *   [MEAS]  re-selected for OUR surfaces at the REF role's measured contrast target,
 *           using the same WCAG/OKLCH math as scripts/validate_palette.js. The measured
 *           ratio is written next to every such value.
 *   [APP]   an existing literal in this codebase (see ./legacy-colour-map.js).
 *   [DERIV] arithmetic on two of the above (stated inline).
 *
 * SURFACE CHOICE (deliberate — the reference's surfaces are warm, ours are cool):
 *   The app's existing chrome is cool-neutral (#f5f6fa page, #788896, #44535f, #c3cfd9),
 *   so the neutral ramp is re-selected cool. It is NOT the reference ramp hue-shifted at
 *   random: each role sits at the reference role's own measured contrast against its own
 *   surface (targets in the comments below).
 *
 *   Light panel  #f7f9fb — NOT #ffffff. All-day sessions over large white table areas are
 *                fatiguing; this is 5.5% below pure white in relative luminance (0.9449 vs
 *                1.0) while still reading as "white paper". Verified: it does not push a
 *                fourth categorical slot into the sub-3:1 relief band — the same three slots
 *                (aqua/yellow/magenta) that are sub-3:1 on the reference surface are the
 *                only three here.
 *   Light canvas #eef1f5 — today's #f5f6fa [APP] stepped one notch deeper (rel. luminance
 *                0.9223 -> 0.8768). It has to go deeper because panels are no longer pure
 *                white; this restores the page/panel read that #f5f6fa + #fff used to give.
 *   Dark panel   #171b21 — cool near-black at OKLCH L 0.2207, within a hair of the
 *                reference dark surface (#1a1a19, L 0.2174), so every REF dark step keeps
 *                the relationship it was validated at. All 8 dark slots clear 3:1 here.
 *   Dark is a SELECTED set validated against the dark surface, never an inversion of light.
 *
 * VALIDATOR RUNS (scripts/validate_palette.js, real output in the change report):
 *   light 8 slots  --surface #f7f9fb : ALL PASS, 1 contrast WARN (3 relief slots)
 *   dark  8 slots  --surface #171b21 : ALL PASS, no WARN
 *   light/dark first 3 slots --pairs all : ALL PASS (series cap for scatter/bubble/map)
 *   light ordinal  --surface #f7f9fb : FAILED at ramp step 250 (2.00:1, floor is 2.0);
 *                  fixed by starting the light ordinal ramp at step 300 (2.37:1).
 */

/* ── type ─────────────────────────────────────────────────────────────────────────────
 * System sans everywhere, including hero figures [REF "Typeface & figures"]. This also
 * fixes an existing bug: index.css asks for -apple-system BEFORE Poppins, so the app
 * already renders San Francisco on macOS and Poppins elsewhere. Naming the system stack
 * makes that intentional and lets public/index.html stop downloading 8 Poppins weights.
 */
const font = {
  sans: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  // [APP] src/index.css `code` rule — kept for hashes, addresses and tx ids.
  mono: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
  weight: { regular: 400, medium: 500, semibold: 600 },
  /**
   * tabular-nums goes ONLY where digits must line up vertically: table cells and axis
   * ticks. Hero/stat-tile figures keep proportional figures — they read better [REF].
   */
  figures: { tabular: 'tabular-nums', proportional: 'normal' },
}

/* ── spacing ──────────────────────────────────────────────────────────────────────────
 * 4px base. The codebase's de-facto rhythm is 4/8/10/12/20 [APP]; 10 is the only value
 * off-grid and folds to 8 (padding) or 12 (gutters). MUI `spacing: 4` => spacing(n) = 4n.
 */
const spacing = {
  base: 4,
  none: 0,
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
}

/* ── radius ───────────────────────────────────────────────────────────────────────────
 * Dense terminal: near-square. MUI's default 4px is the loosest step we use.
 */
const radius = {
  none: 0,
  xs: 2,
  sm: 3,
  md: 4,
  lg: 6,
  pill: 999,
}

/* ── density ──────────────────────────────────────────────────────────────────────────
 * rowHeight 28 is derived, not picked: 12px cell text x 1.4 line-height = 16.8 -> 17,
 * + 2 x 5px vertical padding + 1px hairline = 28. [DERIV]
 * fontSize.table 12 is the midpoint of the two sizes the app already ships: 14px desktop
 * and the 10px it drops to under 768px and in fullscreen [APP: App.css, Table.module.css].
 * fontSize.base 13 is one step below MUI's default 14, per the compact-density brief.
 * cellPaddingY/X 5/8 regularises the current `th { padding: 0.3em 8px }` (~4.2px/8px) [APP].
 */
const density = {
  rowHeight: 28,
  rowHeightHead: 30,
  // What the row virtualizer assumes before a row has been measured. Set slightly above
  // rowHeight because real rows carry controls: measured medians after the Phase 2 density
  // pass are 31-37px across the app. Too low and the scrollbar jumps as rows resolve.
  estimatedRowHeight: 34,
  cellPaddingY: 5,
  cellPaddingX: 8,
  cellPaddingYDense: 3,
  cellPaddingXDense: 6,
  controlHeight: 30,
  controlHeightSmall: 26,
  hairline: 1,
  rule: 2,
  fontSize: {
    micro: 10,
    caption: 11,
    table: 12,
    base: 13,
    lead: 15,
  },
  lineHeight: {
    tight: 1.25,
    table: 1.4,
    base: 1.45,
  },
}

/* ── status — FIXED, never themed, never a series colour ──────────────────────────────
 * Verbatim [REF "Status palette (fixed — never themed)"]. Same four hexes in both modes.
 * Measured against OUR surfaces:
 *                 light #f7f9fb   dark #171b21
 *   good           3.18:1          5.15:1
 *   warning        1.74:1          9.42:1
 *   serious        2.50:1          6.55:1
 *   critical       4.55:1          3.60:1
 *
 * RELIEF RULE (not optional, not dismissable): on the LIGHT surface `warning` (1.74),
 * `serious` (2.50) and `good` (3.18, fine as a graphical mark but under 4.5 as text) do
 * not carry meaning on colour alone. Every status must ship icon + shape + label. On the
 * DARK surface `critical` is 3.60 — a legal mark, but under 4.5 as text: use
 * `delta.down` for coloured status TEXT in dark mode. (Not MUI's runtime-derived
 * `error.light`: lighten(critical, 0.15) lands at 4.47:1 on the dark panel — under the
 * very floor this rule exists to satisfy. src/theme/index.js therefore sets error.light
 * and success.light explicitly from the delta tokens rather than letting augmentColor
 * derive them.)
 * This is exactly the fix for the app's current red/green-only indicators
 * (App.css .red/.green) [APP].
 */
const status = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
}

/**
 * The non-colour half of every status. Consuming code MUST render `icon` + `label`
 * alongside `status[role]`; `shape` is the mark shape for chart/legend use. Keeping this
 * in the token layer is what makes "never colour alone" mechanical instead of a habit.
 * Icon names are @mui/icons-material component names (already a dependency).
 */
const statusMeta = {
  good: { icon: 'CheckCircleOutline', shape: 'circle', label: 'OK' },
  warning: { icon: 'WarningAmberOutlined', shape: 'triangle', label: 'Warning' },
  serious: { icon: 'ReportProblemOutlined', shape: 'diamond', label: 'Serious' },
  critical: { icon: 'ErrorOutline', shape: 'square', label: 'Critical' },
}

/* ── categorical series — FIXED ORDER ─────────────────────────────────────────────────
 * Verbatim [REF "Categorical palette"]. The ORDER is the CVD-safety mechanism, not
 * cosmetics — do not re-order, do not re-step, do not insert. Status colours are never
 * reused here and these are never used as a status.
 */
const series = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
  hues: ['blue', 'orange', 'aqua', 'yellow', 'magenta', 'green', 'violet', 'red'],
  /**
   * Scatter / bubble / choropleth / small multiples put every pair on screen at once and
   * the full eight cannot clear the all-pairs floors. Cap those forms at the first THREE
   * slots (validated --pairs all in both modes against our surfaces); past three, fold to
   * "Other" or facet. Bars, lines and stacks use the adjacent pairlist — all eight are fine.
   */
  allPairsCap: 3,
  /**
   * Sub-3:1 against the LIGHT panel — legal only with relief: visible direct labels or a
   * table view. Never a bare mark in a legend-only chart. (Dark: none, all eight clear 3:1.)
   */
  reliefRequired: {
    light: ['#1baf7a', '#eda100', '#e87ba4'],
    dark: [],
  },
}

/* ── sequential ramp — single hue, blue ─────────────────────────────────────────────── */
const sequential = {
  // Verbatim [REF "Sequential hue"]. Continuous magnitude (heatmaps, choropleths).
  blue: {
    100: '#cde2fb',
    150: '#b7d3f6',
    200: '#9ec5f4',
    250: '#86b6ef',
    300: '#6da7ec',
    350: '#5598e7',
    400: '#3987e5',
    450: '#2a78d6',
    500: '#256abf',
    550: '#1c5cab',
    600: '#184f95',
    650: '#104281',
    700: '#0d366b',
  },
  /**
   * ORDINAL (discrete ordered marks: funnel stages, tiers) — the step nearest the surface
   * must clear 2:1. The reference's light floor is step 250; against OUR light panel that
   * measures 2.00:1 and the validator FAILS it, so our light floor is step 300 (2.37:1).
   * Dark floor is step 600 (2.13:1 here), same as the reference.
   */
  ordinalFloor: { light: 300, dark: 600 },
  // A second simultaneous sequential context takes the next slot's hue (orange) [REF].
  secondHue: 'orange',
}

/* ── diverging — blue <-> red, gray midpoint ──────────────────────────────────────────
 * Verbatim [REF "Diverging pair"]. Warm/cool poles that read as opposite; equal step
 * count per arm. This is for CONTINUOUS diverging scales, not for P&L sign — signed money
 * uses `delta` below, which is green/red by trading convention.
 */
const diverging = {
  light: { negative: '#2a78d6', neutral: '#f0efec', positive: '#e34948' },
  dark: { negative: '#3987e5', neutral: '#383835', positive: '#e66767' },
}

/* ── light mode ───────────────────────────────────────────────────────────────────────
 * Chrome/ink re-selected cool at the reference roles' own measured contrast targets
 * (target = the REF value's ratio against the REF surface #fcfcfb).
 */
const light = {
  mode: 'light',
  surface: {
    canvas: '#eef1f5', // page plane. 1.07:1 vs panel. [APP #f5f6fa stepped one notch deeper]
    panel: '#f7f9fb', // cards, tables, panels — THE validator surface. [MEAS]
    sunken: '#f1f4f8', // wells, filter rows, input rest. 1.05:1 [MEAS]
    raised: '#ffffff', // menus, popovers, dialogs ONLY — transient, so pure white is fine
    header: '#e4e9f0', // table head. 1.16:1 [MEAS] — replaces the #696969/white head [APP]
    inverse: '#0e131a', // dark-on-light tooltips
    backdrop: 'rgba(14, 19, 26, 0.48)', // [DERIV] ink.primary at 48%
  },
  ink: {
    primary: '#0e131a', // 17.66:1 (REF role 19.17:1) [MEAS]
    secondary: '#454f5c', // 7.88:1 (REF role 7.73:1) [MEAS] — replaces rgba(0,0,0,0.54) [APP]
    muted: '#7d8794', // 3.45:1 (REF role 3.50:1) [MEAS] — axis ticks, placeholders
    disabled: '#9aa3ae', // 2.42:1 [MEAS] — non-text only
    onAccent: '#ffffff', // 6.63:1 on accent.main [MEAS]
    onInverse: '#eef2f7', // on surface.inverse
  },
  border: {
    hairline: '#dae0e8', // 1.26:1 (REF gridline 1.29:1) [MEAS] — the table grid
    default: '#ccd3dd', // 1.43:1 [MEAS] — panel rings, input outlines
    strong: '#b9c1cc', // 1.72:1 (REF baseline/axis 1.75:1) [MEAS] — axis, head underrule
    focus: '#1c5cab', // = accent.main
  },
  interaction: {
    hover: '#e9eef5', // 1.10:1 [MEAS] — neutral ghost wash; replaces #d6eaff [APP]
    selected: '#cde2fb', // sequential.blue.100 [REF] — 1.25:1; hue distinguishes it from hover
    selectedHover: '#b7d3f6', // sequential.blue.150 [REF]
    focusRing: 'rgba(28, 92, 171, 0.40)', // [DERIV] accent.main at 40%
  },
  accent: {
    main: '#1c5cab', // sequential.blue.550 [REF] — 6.28:1 as link text, 6.63:1 white on it
    light: '#2a78d6', // sequential.blue.450 = series slot 1 [REF] — replaces #1976d2 [APP]
    dark: '#184f95', // sequential.blue.600 [REF]
    contrastText: '#ffffff',
  },
  /**
   * Signed money / P&L direction. `up` is verbatim [REF chrome table, "Delta up (success
   * text)"]. `down` has no published step, so it is the fixed status-critical hue #d03b3b
   * darkened along its own hue until it clears the 4.5:1 body-text floor: #b8332f = 5.60:1
   * [MEAS]. It sits ΔE 10.1 from the light series red #e34948 and ΔE 5.3 from
   * status-critical — same family by design.
   * Delta colour still never rides alone: always ship the sign and a directional arrow.
   */
  delta: { up: '#006300', down: '#b8332f', flat: '#454f5c' },
  chart: {
    surface: '#f7f9fb',
    gridline: '#dae0e8',
    axis: '#b9c1cc',
    tick: '#7d8794',
    series: series.light,
    sequential: 'blue',
  },
  shadow: {
    ambient: 'rgba(14, 19, 26, 0.06)',
    key: 'rgba(14, 19, 26, 0.10)',
  },
}

/* ── dark mode — SELECTED against the dark surface, not an inversion ─────────────────── */
const dark = {
  mode: 'dark',
  surface: {
    canvas: '#0e1116',
    panel: '#171b21', // THE validator surface. OKLCH L 0.2207 (REF dark surface 0.2174)
    sunken: '#12161b', // 1.05:1 [MEAS]
    raised: '#1c212a', // 1.07:1 [MEAS] — menus, popovers, dialogs
    header: '#242a34', // 1.20:1 [MEAS] — table head
    inverse: '#eef2f7',
    backdrop: 'rgba(14, 17, 22, 0.70)', // [DERIV] surface.canvas at 70%
  },
  ink: {
    // REF says #ffffff. Stepped down one notch for an all-day console: pure white on a
    // near-black surface halates. 15.37:1 (REF role 17.42:1) [MEAS].
    primary: '#eef2f7',
    secondary: '#b9c2cd', // 9.60:1 (REF role 9.72:1) [MEAS]
    muted: '#7f8a97', // 4.92:1 (REF role 4.85:1) [MEAS]
    disabled: '#5c6673', // 2.97:1 [MEAS] — non-text only
    onAccent: '#0e131a', // 6.24:1 on accent.main [MEAS]
    onInverse: '#0e131a',
  },
  border: {
    hairline: '#272d36', // 1.25:1 (REF gridline 1.24:1) [MEAS]
    default: '#333a44', // 1.51:1 [MEAS]
    strong: '#3a434f', // 1.72:1 (REF baseline 1.48:1, raised for a cool surface) [MEAS]
    focus: '#5598e7',
  },
  interaction: {
    hover: '#1f242b', // 1.11:1 [MEAS] — neutral ghost wash
    selected: '#0d366b', // sequential.blue.700 [REF] — 1.45:1
    selectedHover: '#104281', // sequential.blue.650 [REF] — 1.74:1
    focusRing: 'rgba(85, 152, 231, 0.45)', // [DERIV] accent.main at 45%
  },
  accent: {
    main: '#5598e7', // sequential.blue.350 [REF] — 5.79:1 as text
    light: '#86b6ef', // sequential.blue.250 [REF]
    dark: '#3987e5', // sequential.blue.400 = series slot 1 dark [REF]
    contrastText: '#0e131a',
  },
  /**
   * `up` is verbatim [REF chrome table dark]. `down` is the fixed status-critical hue
   * #d03b3b lightened along its own hue until it clears the 4.5:1 body-text floor on the
   * dark panel: #e84f4c = 4.65:1 [MEAS] (status-critical itself is only 3.60:1 there).
   * It lands ΔE 4.5 from the dark series red #e66767 — the same order of adjacency the
   * reference itself accepts (its light red vs critical measures 4.8) and it is covered by
   * the same obligation: sign + arrow + placement, never hue alone. A series red never
   * appears as a signed figure, and a delta figure is never a chart mark.
   */
  delta: { up: '#0ca30c', down: '#e84f4c', flat: '#b9c2cd' },
  chart: {
    surface: '#171b21',
    gridline: '#272d36',
    axis: '#3a434f',
    tick: '#7f8a97',
    series: series.dark,
    sequential: 'blue',
  },
  shadow: {
    ambient: 'rgba(0, 0, 0, 0.45)',
    key: 'rgba(0, 0, 0, 0.30)',
  },
}

export const MODES = ['light', 'dark']
export const DEFAULT_MODE = 'light'

const tokens = {
  font,
  spacing,
  radius,
  density,
  status,
  statusMeta,
  series,
  sequential,
  diverging,
  light,
  dark,
}

export { font, spacing, radius, density, status, statusMeta, series, sequential, diverging, light, dark }

export default tokens
